import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { Lecture } from '@/types';

interface LectureState {
  lectures: Lecture[];
  currentLecture: Lecture | null;
  currentSectionIndex: number;
  isLoading: boolean;
  error: string | null;
  setLectures: (lectures: Lecture[]) => void;
  setCurrentLecture: (lecture: Lecture | null) => void;
  setCurrentSectionIndex: (index: number) => void;
  fetchLectures: (userId: string) => Promise<void>;
  generateLecture: (userId: string, documentIds: string[], title: string, description?: string) => Promise<Lecture | null>;
  markSectionComplete: (lectureId: string, sectionIndex: number) => Promise<void>;
  deleteLecture: (lectureId: string) => Promise<void>;
}

export const useLectureStore = create<LectureState>((set, get) => ({
  lectures: [],
  currentLecture: null,
  currentSectionIndex: 0,
  isLoading: false,
  error: null,

  setLectures: (lectures) => set({ lectures }),
  setCurrentLecture: (lecture) => set({ currentLecture: lecture, currentSectionIndex: 0 }),
  setCurrentSectionIndex: (index) => set({ currentSectionIndex: index }),

  fetchLectures: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('lectures')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchLectures');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ lectures: data || [], isLoading: false });
  },

  generateLecture: async (userId: string, documentIds: string[], title: string, description?: string) => {
    return Sentry.startSpan(
      { name: 'lecture.generate', op: 'ai.generation', attributes: { 'documents.count': documentIds.length } },
      async () => {
    set({ isLoading: true, error: null });

    // 1. Create lecture row with status: 'generating'
    const { data: newLecture, error: insertError } = await supabase
      .from('lectures')
      .insert({
        user_id: userId,
        title,
        description,
        document_ids: documentIds,
        sections: [],
        reading_time_minutes: 0,
        completed_sections: [],
        status: 'generating',
      })
      .select()
      .single();

    if (insertError || !newLecture) {
      set({ error: insertError?.message || 'Failed to create lecture', isLoading: false });
      return null;
    }

    // 2. Add to local state immediately
    set((state) => ({
      lectures: [newLecture, ...state.lectures],
      isLoading: false,
    }));

    // 3. Fire-and-forget edge function
    supabase.functions.invoke('generate-lecture', {
      body: { lectureId: newLecture.id, documentIds, title, description },
    }).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { context: 'lectureStore.generateLecture' } });
    });

    return newLecture;
      });
  },

  markSectionComplete: async (lectureId: string, sectionIndex: number) => {
    const lecture = get().lectures.find((l) => l.id === lectureId);
    if (!lecture) return;

    const completedSections = [...new Set([...lecture.completed_sections, sectionIndex])];

    const { error } = await supabase
      .from('lectures')
      .update({ completed_sections: completedSections })
      .eq('id', lectureId);

    if (error) {
      reportSupabaseError(error, 'lectureStore.markSectionComplete');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      lectures: state.lectures.map((l) =>
        l.id === lectureId ? { ...l, completed_sections: completedSections } : l
      ),
      currentLecture:
        state.currentLecture?.id === lectureId
          ? { ...state.currentLecture, completed_sections: completedSections }
          : state.currentLecture,
    }));
  },

  deleteLecture: async (lectureId: string) => {
    const { error } = await supabase
      .from('lectures')
      .delete()
      .eq('id', lectureId);

    if (error) {
      reportSupabaseError(error, 'lectureStore.deleteLecture');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      lectures: state.lectures.filter((l) => l.id !== lectureId),
      currentLecture: state.currentLecture?.id === lectureId ? null : state.currentLecture,
    }));
  },
}));
